import { ThemeEvents, CartUpdateEvent } from '@theme/events';

if (!window.customElements.get('cart-progress')) {
  window.customElements.define(
    'cart-progress',
    class CartProgress extends HTMLElement {
      constructor() {
        super();
        this.debounceTimeout = null;
      }

      connectedCallback() {
        // Initial draw: use data-progress only (no fetch)
        this.animateCart(false);

        document.addEventListener('cart:update', () => {
          clearTimeout(this.debounceTimeout);
          this.debounceTimeout = setTimeout(() => this.animateCart(true), 650);
        });
      }

      async fetchCart() {
        try {
          const res = await fetch('/cart.js');
          if (!res.ok) throw new Error('Cart fetch failed');
          return await res.json();
        } catch (error) {
          console.error('Error fetching cart:', error);
          return null;
        }
      }

      updateFreeShippingMessage(cartTotal, freeShippingThreshold) {
        const freeShippingMessage = this.querySelector('.free-shipping-message');
        if (!freeShippingMessage || !freeShippingThreshold) return;

        const freeShippingGap = Math.max(0, freeShippingThreshold - cartTotal);
        const messageText = freeShippingMessage.querySelector('.free-shipping-text');
        
        if (!messageText) return;

        if (freeShippingGap > 0) {
          const gapInDollars = Math.round(freeShippingGap / 100);
          messageText.textContent = `You're $${gapInDollars} away from free shipping!`;
          messageText.classList.remove('free-shipping-achieved');
        } else {
          messageText.textContent = 'Congrats! Your order qualifies for free shipping.';
          messageText.classList.add('free-shipping-achieved');
        }
      }

      async animateCart(fromEvent) {
        const progressBar = this.querySelector('.ns-progress');
        let cart = null;

        if (fromEvent) {
          cart = await this.fetchCart();
        }

        const cartTotal = fromEvent
          ? cart?.total_price || 0
          : parseFloat(progressBar?.dataset.progress || 0);

        // ALWAYS update free shipping message and progress bar FIRST
        const freeShippingMessage = this.querySelector('.free-shipping-message');
        const progressFill = this.querySelector('.ns-progress-fill');
        
        if (freeShippingMessage && progressFill) {
          const freeShippingThreshold = parseFloat(freeShippingMessage.dataset.freeShippingThreshold || 0);
          this.updateFreeShippingMessage(cartTotal, freeShippingThreshold);
          
          // Update progress bar based on free shipping threshold
          if (freeShippingThreshold > 0) {
            const progressPercentage = Math.min(100, (cartTotal / freeShippingThreshold) * 100);
            setTimeout(() => {
              progressFill.style.width = `${progressPercentage}%`;
            }, 200);
          }
        }

        const targetGiftsContainer = this.querySelector('.target-gifts');
        const locks = this.querySelectorAll('.target-locks .target');

        // Gift blocks - only process if they exist
        const giftBlocks = Array.from(this.querySelectorAll('.ns-target-gift'));
        
        if (giftBlocks.length > 0) {
          const existingGifts = new Set();
          giftBlocks.forEach((gift) => {
            const id = gift.dataset.giftId;
            if (!existingGifts.has(id)) {
              if (!targetGiftsContainer.contains(gift)) {
                targetGiftsContainer.appendChild(gift);
              }
              existingGifts.add(id);
            }
          });

          targetGiftsContainer.classList.add(`size-${giftBlocks.length}`);

          // Progress bar logic for gift targets (only if we have gift blocks)
          const gap = 100;
          let currentTarget = null;
          let nextTarget = null;

          locks.forEach((lock, i) => {
            const targetVal = parseFloat(lock.dataset.target);
            if (cartTotal >= targetVal) {
              currentTarget = lock;
              nextTarget = locks[i + 1];
              setTimeout(() => {
                lock.classList.add('active');
              }, 300 * i);
            }
          });

          if (currentTarget && nextTarget && progressFill) {
            const cVal = parseFloat(currentTarget.dataset.target);
            const nVal = parseFloat(nextTarget.dataset.target);
            const extraVal = (cartTotal - cVal) / (nVal - cVal);
            const barIndex = Array.from(locks).indexOf(currentTarget);
            const barWidth = barIndex * gap + gap * extraVal;
            setTimeout(() => {
              progressFill.style.width = `${barWidth}%`;
            }, 200);
          }
        }

        // Gift logic (only run if we have cart data and gift blocks)
        if (cart && giftBlocks.length > 0) {
          const updates = {};
          let needsUpdate = false;

          for (let gift of giftBlocks) {
            const giftTarget = parseFloat(gift.dataset.giftTarget);
            const giftId = parseInt(gift.dataset.giftId, 10);
            const classList = gift.classList;

            const hasGift = cart.items?.some((item) => item.variant_id === giftId);
            const noAppGift = classList.contains('noappgift');
            const isMultiGift = classList.contains('multigift');

            // Flip visibility of gift messages
            const giftAddedEl = gift.querySelector('[data-gift-added-message]');
            const giftNotAddedEl = gift.querySelector('[data-gift-not-added-message]');
            if (giftAddedEl) giftAddedEl.style.display = hasGift ? 'inline' : 'none';
            if (giftNotAddedEl) giftNotAddedEl.style.display = hasGift ? 'none' : 'inline';

            // Only push an update if state is wrong
            if (cartTotal >= giftTarget && !hasGift && noAppGift) {
              updates[giftId] = 1;
              needsUpdate = true;
            } else if (cartTotal < giftTarget && hasGift && noAppGift) {
              updates[giftId] = 0;
              needsUpdate = true;
            } else if (isMultiGift && !hasGift) {
              updates[giftId] = 1;
              needsUpdate = true;
            }
          }

          if (needsUpdate) {
            this.updateCart(updates);
          }
        }
      }

      updateCart(updates) {
        const formData = new URLSearchParams();

        for (const [id, qty] of Object.entries(updates)) {
          formData.append(`updates[${id}]`, qty);
        }

        const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
          .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
          .filter(Boolean);

        if (sectionIds.length) {
          formData.append('sections', sectionIds.join(','));
        }

        fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
        })
          .then((res) => res.json())
          .then((data) => {
            const event = new CartUpdateEvent({ data });
            document.dispatchEvent(event);
          })
          .catch((err) => console.error('Cart update error:', err));
      }
    }
  );
}